import * as React from 'react';
import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Chip, Paper, Switch, TextField, Tooltip } from '@mui/material';
import { sendServerRequest } from '../../../server/request';
import { openGlobalSnack } from '../../utils/snack';
import { CircularLoading } from '../../utils/loading';

const ENDPOINT = '/server/cltCommand/consultHour';

type Provider = 'v8' | 'suma_celcoin' | 'suma_uy3';

interface HourWindow {
  /** Desligado = provedor consulta 24h. */
  enabled: boolean;
  /** Hora inicial (0-23), inclusiva, horário de Brasília. */
  start: number;
  /** Hora final (1-24), exclusiva, horário de Brasília. */
  end: number;
}

type HourConfig = Record<Provider, HourWindow>;

const PROVIDERS: Provider[] = ['v8', 'suma_celcoin', 'suma_uy3'];

const LABELS: Record<Provider, string> = {
  v8: 'V8',
  suma_celcoin: 'Suma · Celcoin',
  suma_uy3: 'Suma · UY3',
};

const EMPTY: HourConfig = {
  v8: { enabled: false, start: 0, end: 24 },
  suma_celcoin: { enabled: false, start: 0, end: 24 },
  suma_uy3: { enabled: false, start: 0, end: 24 },
};

/** Descrição da janela como o servidor a interpreta (inclusive virando a meia-noite). */
function windowText(w: HourWindow): string {
  if (!w.enabled) return 'Consulta 24h por dia';
  if (w.start === w.end) return 'Janela cheia (24h)';
  if (w.start < w.end) return `Consulta das ${w.start}h às ${w.end}h`;
  return `Consulta das ${w.start}h às ${w.end}h do dia seguinte`;
}

function isValidHour(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 24;
}

export function ConsultHourPanel() {
  const [config, setConfig] = useState<HourConfig>(EMPTY);
  const [outOfHour, setOutOfHour] = useState<string[]>([]);
  const [serverSnapshot, setServerSnapshot] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  function applyResponse(data: any) {
    const c: HourConfig = { ...EMPTY, ...(data?.config ?? {}) };
    setConfig(c);
    setOutOfHour(data?.outOfHour ?? []);
    setServerSnapshot(JSON.stringify(c));
  }

  function load() {
    setLoading(true);
    sendServerRequest('GET', `${ENDPOINT}/get`)
      .then((e) => {
        applyResponse(e.data?.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar os horários de consulta.', 'error');
      });
  }

  function setWindow(provider: Provider, patch: Partial<HourWindow>) {
    setConfig((prev) => ({ ...prev, [provider]: { ...prev[provider], ...patch } }));
  }

  function setHour(provider: Provider, key: 'start' | 'end', value: string) {
    const n = parseInt(value, 10);
    setWindow(provider, { [key]: Number.isFinite(n) ? n : 0 } as Partial<HourWindow>);
  }

  function save() {
    // O servidor recusa hora fora de 0-24; barra aqui para o erro chegar no campo.
    const invalid = PROVIDERS.find(
      (p) => !isValidHour(config[p].start) || !isValidHour(config[p].end),
    );
    if (invalid) {
      openGlobalSnack(`Horário inválido em ${LABELS[invalid]}: use 0 a 24.`, 'error');
      return;
    }

    setSaving(true);
    sendServerRequest('POST', `${ENDPOINT}/set`, { config })
      .then((e) => {
        applyResponse(e.data?.data);
        setSaving(false);
        openGlobalSnack('Horários de consulta atualizados.', 'success');
      })
      .catch(() => {
        setSaving(false);
        openGlobalSnack('Não foi possível salvar os horários.', 'error');
      });
  }

  const dirty = JSON.stringify(config) !== serverSnapshot;

  if (loading) return <CircularLoading />;

  return (
    <div style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
      <h3 style={{ marginBottom: 4 }}>Horário de consulta por provedor</h3>
      <p style={{ fontSize: 12, color: '#000000a0', marginTop: 0, marginBottom: 16 }}>
        Cada provedor tem a própria janela (horário de Brasília). Fora dela o provedor
        é pulado e a consulta segue nos outros — a V8 consulta 24h por dia. O cliente
        só recebe "fora de horário" quando NENHUM provedor habilitado está na janela.
        Início é inclusivo e fim é exclusivo (8 → 22 = das 8h às 21h59); fim menor que
        o início atravessa a meia-noite.
      </p>

      {PROVIDERS.map((p) => {
        const w = config[p];
        const isOut = outOfHour.includes(p);
        return (
          <Paper
            key={p}
            variant="outlined"
            style={{ padding: '8px 12px', marginBottom: 8, textAlign: 'left' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong>{LABELS[p]}</strong>
                {isOut && (
                  <Tooltip title="Segundo a configuração salva, este provedor não é consultado agora.">
                    <Chip label="fora da janela agora" size="small" color="warning" />
                  </Tooltip>
                )}
              </div>
              <Tooltip title={w.enabled ? 'Limite de horário ligado' : 'Sem limite: consulta 24h'}>
                <Switch
                  checked={w.enabled}
                  onChange={(e) => setWindow(p, { enabled: e.target.checked })}
                />
              </Tooltip>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
              <TextField
                label="Início"
                type="number"
                size="small"
                value={w.start}
                disabled={!w.enabled}
                onChange={(e) => setHour(p, 'start', e.target.value)}
                inputProps={{ min: 0, max: 24 }}
                style={{ maxWidth: 110 }}
              />
              <TextField
                label="Fim"
                type="number"
                size="small"
                value={w.end}
                disabled={!w.enabled}
                onChange={(e) => setHour(p, 'end', e.target.value)}
                inputProps={{ min: 0, max: 24 }}
                style={{ maxWidth: 110 }}
              />
              <span style={{ fontSize: 12, color: '#000000a0' }}>{windowText(w)}</span>
            </div>
          </Paper>
        );
      })}

      <div style={{ marginTop: 16 }}>
        <LoadingButton loading={saving} disabled={!dirty} variant="contained" onClick={save}>
          Salvar horários
        </LoadingButton>
      </div>
    </div>
  );
}
